import type { EditableScalarKind, TaggedValue } from '@axilon-apps/domains-ontology-ui-state'
import { taggedScalarText } from './conflict-view-model'

export interface ConflictValueEditorState {
  field: string
  kind: EditableScalarKind
  text: string
  error?: string
}

export function ConflictValueEditor({
  editing,
  interactive,
  onEditingChange,
  onConfirm,
  onCancel
}: {
  editing: ConflictValueEditorState
  interactive: boolean
  onEditingChange: (editing: ConflictValueEditorState) => void
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <div className="col-span-5 mt-0.5 flex flex-wrap items-center gap-1.5 rounded-sm bg-brand-soft/40 px-1.5 py-1">
      <span className="font-mono text-10 text-ink-3">custom value</span>
      {editing.kind === 'boolean' ? (
        <select
          value={editing.text}
          disabled={!interactive}
          onChange={(e) =>
            onEditingChange({
              field: editing.field,
              kind: editing.kind,
              text: e.target.value
            })
          }
          aria-label={`Custom value — ${editing.field}`}
          className="rounded-sm border border-line bg-bg px-1 py-0.5 font-mono text-11 text-ink"
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      ) : (
        <input
          autoFocus
          value={editing.text}
          disabled={!interactive}
          inputMode={
            editing.kind === 'int' ? 'numeric' : editing.kind === 'float' ? 'decimal' : 'text'
          }
          onChange={(e) =>
            onEditingChange({
              field: editing.field,
              kind: editing.kind,
              text: e.target.value
            })
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onConfirm()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              onCancel()
            }
          }}
          aria-label={`Custom value — ${editing.field}`}
          className="min-w-0 flex-1 rounded-sm border border-line bg-bg px-1 py-0.5 font-mono text-11 text-ink"
        />
      )}
      <button
        type="button"
        disabled={!interactive}
        onClick={onConfirm}
        className="rounded-sm bg-brand-soft px-1.5 py-0.5 text-10 font-medium text-brand hover:bg-brand-soft/70"
      >
        Use value
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="rounded-sm px-1.5 py-0.5 text-10 text-ink-3 hover:text-ink"
      >
        Cancel
      </button>
      {editing.error ? <span className="text-10 text-st-down">{editing.error}</span> : null}
    </div>
  )
}

export function ConflictCustomValueSummary({
  value,
  interactive,
  canEdit,
  onEdit
}: {
  value: TaggedValue
  interactive: boolean
  canEdit: boolean
  onEdit: () => void
}) {
  return (
    <div className="col-span-5 mt-0.5 flex items-center gap-1.5 rounded-sm bg-brand-soft px-1.5 py-1">
      <span className="text-10 font-medium tracking-wide text-brand uppercase">Custom</span>
      <span
        className="min-w-0 flex-1 truncate font-mono text-11 text-brand"
        title={taggedScalarText(value)}
      >
        {taggedScalarText(value)}
      </span>
      {interactive && canEdit ? (
        <button
          type="button"
          onClick={onEdit}
          className="rounded-sm px-1.5 py-0.5 text-10 text-brand hover:underline"
        >
          Edit
        </button>
      ) : null}
    </div>
  )
}
